import { useCallback, useEffect, useState } from "react";
import { Button, Input } from "antd";
import { useSelector } from "react-redux";
import { FileTextOutlined, RobotOutlined, SaveOutlined } from "@ant-design/icons";
import isEmpty from "../validations/is-empty";
import openNotification from "../components/Notifications/openNotification";
import apiClient from "../actions/api";
import "../style/promptpage.scss";

const { TextArea } = Input;

const PromptEditor = ({ icon, title, description, value, onChange, disabled, placeholders }) => (
  <section className="prompt-editor-card">
    <div className="prompt-panel-header">
      <div className="prompt-card-title">
        <span className="prompt-card-icon">{icon}</span>
        <div><h3>{title}</h3><p>{description}</p></div>
      </div>
      <span className={`prompt-access-badge ${disabled ? "read-only" : "can-edit"}`}>
        {disabled ? "Read only" : "Active"}
      </span>
    </div>
    <div className="prompt-placeholders" aria-label={`${title} variables`}>
      <span>Available variables</span>
      {placeholders.map((placeholder) => <code key={placeholder}>{placeholder}</code>)}
    </div>
    <TextArea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      className="update-textarea"
      autoSize={{ minRows: 18, maxRows: 32 }}
    />
    <div className="prompt-character-count">{value.length.toLocaleString()} characters</div>
  </section>
);

const PromptPage = ({ title }) => {
  document.title = title;
  const user = useSelector((state) => state?.auth?.user);
  const themeMode = useSelector((state) => state.themeMode);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [resumeDocumentPrompt, setResumeDocumentPrompt] = useState("");
  const canEdit = user?.role === 1;

  const showErrors = useCallback((error) => {
    const errors = error?.response?.data?.errors;
    if (!isEmpty(errors)) {
      errors.forEach((item) => openNotification("error", "Validation Error!", item.msg));
    } else {
      openNotification("error", "Error", "Unable to load or update AI prompts.");
    }
  }, []);

  const getData = useCallback(() => {
    setLoading(true);
    apiClient.get("/prompts/get")
      .then((response) => {
        setPrompt(response.data?.prompt?.prompt || "");
        setResumeDocumentPrompt(response.data?.prompt?.resume_document_prompt || "");
      })
      .catch(showErrors)
      .finally(() => setLoading(false));
  }, [showErrors]);

  useEffect(() => { getData(); }, [getData]);

  const handleUpdatePrompt = () => {
    if (!prompt.trim() || !resumeDocumentPrompt.trim()) {
      openNotification("error", "Prompts required", "Enter both AI prompts before saving.");
      return;
    }
    setLoading(true);
    apiClient.post("/prompts/update", {
      prompt,
      resume_document_prompt: resumeDocumentPrompt,
    })
      .then(() => {
        openNotification("success", "Saved", "AI prompts updated successfully.");
        getData();
      })
      .catch(showErrors)
      .finally(() => setLoading(false));
  };

  return (
    <div className={`prompt-section ${themeMode.themeMode ? "prompt-section--dark" : "prompt-section--light"}`}>
      <header className="prompt-page-header">
        <div className="prompt-page-heading">
          <span className="prompt-page-icon"><RobotOutlined /></span>
          <div><h2 className="title">AI Prompt Studio</h2><p>Manage the latest instructions used by both resume workflows.</p></div>
        </div>
        {canEdit && (
          <Button type="primary" className="prompt-save-button" loading={loading} onClick={handleUpdatePrompt} icon={<SaveOutlined />}>
            Save both prompts
          </Button>
        )}
      </header>
      <div className="prompt-workflow-note">
        <strong>Two workflows, one place.</strong>
        <span>Changes apply to the next resume. Previous versions remain stored for auditing but are no longer displayed here.</span>
      </div>
      <div className="prompt-main-section">
        <PromptEditor
          icon={<RobotOutlined />}
          title="Template resume generation"
          description="Creates structured content for variable-based resume templates."
          value={prompt}
          onChange={setPrompt}
          disabled={!canEdit}
          placeholders={["{{JOB_DESCRIPTION}}", "{{EMPLOYMENT_PREFERENCES}}", "{{REPLACE_JSON_EXPERIENCES}}", "{{YEARS_OF_EXPERIENCE}}"]}
        />
        <PromptEditor
          icon={<FileTextOutlined />}
          title="Profile document optimization"
          description="Optimizes an uploaded resume profile while preserving its DOCX layout."
          value={resumeDocumentPrompt}
          onChange={setResumeDocumentPrompt}
          disabled={!canEdit}
          placeholders={["{{JOB_DESCRIPTION}}", "{{JOB_URL}}", "{{RESUME_CONTENT}}"]}
        />
      </div>
    </div>
  );
};

export default PromptPage;
